import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Box, Button, Card, CardContent, CardMedia, Typography, Grid, IconButton, Slider, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, FormGroup, Chip, TextField, CircularProgress } from '@mui/material';
import { Search, ShoppingCart, User, Heart, Star, Filter, Home } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
function ShopPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    priceRange: [0, 500],
    categories: [],
    ratings: [],
    brands: [],
  });
  const [sortBy, setSortBy] = useState('popular');
  const [searchQuery, setSearchQuery] = useState('');
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef();

  // Expanded dummy data for lazy loading
  const allProducts = [
    { id: 1, title: 'Wireless Headphones', price: 150, rating: 4.5, brand: 'Sony', category: 'Electronics', image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&h=300&fit=crop' },
    { id: 2, title: 'Running Shoes', price: 120, rating: 4.0, brand: 'Nike', category: 'Sports', image: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=300&h=300&fit=crop' },
    { id: 3, title: 'Smart Watch', price: 250, rating: 4.8, brand: 'Apple', category: 'Electronics', image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=300&h=300&fit=crop' },
    { id: 4, title: 'Leather Jacket', price: 200, rating: 3.5, brand: 'Levi\'s', category: 'Men', image: 'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=300&h=300&fit=crop' },
    { id: 5, title: 'Yoga Mat', price: 50, rating: 4.2, brand: 'Adidas', category: 'Sports', image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=300&h=300&fit=crop' },
    { id: 6, title: 'Sunglasses', price: 80, rating: 4.1, brand: 'Ray-Ban', category: 'Accessories', image: 'https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=300&h=300&fit=crop' },
    { id: 7, title: 'Coffee Maker', price: 100, rating: 4.3, brand: 'Keurig', category: 'Home', image: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=300&h=300&fit=crop' },
    { id: 8, title: 'Backpack', price: 60, rating: 3.8, brand: 'JanSport', category: 'Accessories', image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=300&h=300&fit=crop' },
    { id: 9, title: 'Gaming Mouse', price: 45, rating: 4.6, brand: 'Logitech', category: 'Electronics', image: 'https://images.unsplash.com/photo-1527814050087-3793815479db?w=300&h=300&fit=crop' },
    { id: 10, title: 'Tennis Racket', price: 180, rating: 4.3, brand: 'Wilson', category: 'Sports', image: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=300&h=300&fit=crop' },
    { id: 11, title: 'Dress Shirt', price: 75, rating: 4.0, brand: 'Hugo Boss', category: 'Men', image: 'https://images.unsplash.com/photo-1598033129183-c4f50c736f10?w=300&h=300&fit=crop' },
    { id: 12, title: 'Skincare Set', price: 95, rating: 4.7, brand: 'The Ordinary', category: 'Beauty', image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=300&h=300&fit=crop' },
    { id: 13, title: 'Bluetooth Earbuds', price: 130, rating: 4.4, brand: 'Jabra', category: 'Electronics', image: 'https://images.unsplash.com/photo-1572569511254-d8f925fe2cbb?w=300&h=300&fit=crop' },
    { id: 14, title: 'Hiking Boots', price: 160, rating: 4.2, brand: 'Salomon', category: 'Sports', image: 'https://images.unsplash.com/photo-1544966503-7cc5ac882d5e?w=300&h=300&fit=crop' },
    { id: 15, title: 'Wrist Watch', price: 220, rating: 4.1, brand: 'Fossil', category: 'Accessories', image: 'https://images.unsplash.com/photo-1524592094714-0f0654e20314?w=300&h=300&fit=crop' },
    { id: 16, title: 'Blender', price: 85, rating: 4.5, brand: 'KitchenAid', category: 'Home', image: 'https://images.unsplash.com/photo-1570222094114-d054a817e56b?w=300&h=300&fit=crop' },
  ];

  const categories = ['Men', 'Women', 'Electronics', 'Accessories', 'Home', 'Sports', 'Beauty'];
  const brands = ['Sony', 'Nike', 'Apple', 'Levi\'s', 'Adidas', 'Ray-Ban', 'Keurig', 'JanSport', 'Logitech', 'Wilson', 'Hugo Boss', 'The Ordinary', 'Jabra', 'Salomon', 'Fossil', 'KitchenAid'];
  const ratings = [5, 4, 3, 2, 1];

  // Lazy loading setup
  const PRODUCTS_PER_LOAD = 8;

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let filtered = allProducts.filter(product => {
      const inPriceRange = product.price >= filters.priceRange[0] && product.price <= filters.priceRange[1];
      const inCategory = filters.categories.length === 0 || filters.categories.includes(product.category);
      const inRating = filters.ratings.length === 0 || filters.ratings.some(r => Math.floor(product.rating) >= r);
      const inBrand = filters.brands.length === 0 || filters.brands.includes(product.brand);
      const matchesSearch = product.title.toLowerCase().includes(searchQuery.toLowerCase());
      return inPriceRange && inCategory && inRating && inBrand && matchesSearch;
    });

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'lowToHigh':
          return a.price - b.price;
        case 'highToLow':
          return b.price - a.price;
        case 'popular':
        default:
          return b.rating - a.rating;
      }
    });

    return filtered;
  }, [filters, sortBy, searchQuery]);

  // Initialize displayed products
  useEffect(() => {
    setDisplayedProducts(filteredProducts.slice(0, PRODUCTS_PER_LOAD));
    setHasMore(filteredProducts.length > PRODUCTS_PER_LOAD);
  }, [filteredProducts]);

  // Load more products
  const loadMoreProducts = useCallback(() => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    setTimeout(() => {
      const currentLength = displayedProducts.length;
      const nextProducts = filteredProducts.slice(currentLength, currentLength + PRODUCTS_PER_LOAD);
      
      setDisplayedProducts(prev => [...prev, ...nextProducts]);
      setHasMore(currentLength + nextProducts.length < filteredProducts.length);
      setIsLoading(false);
    }, 1000); // Simulate loading delay
  }, [displayedProducts, filteredProducts, isLoading, hasMore]);

  // Intersection Observer for lazy loading
  const lastProductRef = useCallback(node => {
    if (isLoading) return;
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreProducts();
      }
    });
    
    if (node) observerRef.current.observe(node);
  }, [isLoading, hasMore, loadMoreProducts]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const handleCategoryChange = (category) => {
    setFilters(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const handleRatingChange = (rating) => {
    setFilters(prev => ({
      ...prev,
      ratings: prev.ratings.includes(rating)
        ? prev.ratings.filter(r => r !== rating)
        : [...prev.ratings, rating]
    }));
  };

  const handleBrandChange = (brand) => {
    setFilters(prev => ({
      ...prev,
      brands: prev.brands.includes(brand)
        ? prev.brands.filter(b => b !== brand)
        : [...prev.brands, brand]
    }));
  };

  return (
    <Box sx={{ mt: 7, mx: 2 }}>
      {/* Simple Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, p: 2, bgcolor: 'white', borderRadius: 1, boxShadow: 1 }}>
        <Button startIcon={<Home />} onClick={() => navigate('/Ecommerce')}>Home</Button>
        <Typography variant="h5">Shop</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={() => navigate('/CartPage')}><ShoppingCart /></IconButton>
          <IconButton onClick={() => navigate('/UserDashboard')}><User /></IconButton>
        </Box>
      </Box>
      {/* Top Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, p: 2, bgcolor: 'white', borderRadius: 1, boxShadow: 1 }}>
        <Typography variant="h6">{filteredProducts.length} Products</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search products..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <Search size={20} />,
            }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Sort by</InputLabel>
            <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <MenuItem value="popular">Popular</MenuItem>
              <MenuItem value="lowToHigh">Price: Low to High</MenuItem>
              <MenuItem value="highToLow">Price: High to Low</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* Filters Panel */}
        <Grid item xs={12} md={3}>
          <Card sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Filter size={20} /> Filters
            </Typography>

            {/* Price Range */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Price Range</Typography>
              <Slider
                value={filters.priceRange}
                onChange={(e, newValue) => handleFilterChange('priceRange', newValue)}
                valueLabelDisplay="auto"
                min={0}
                max={500}
                sx={{ color: 'primary.main' }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">${filters.priceRange[0]}</Typography>
                <Typography variant="body2">${filters.priceRange[1]}</Typography>
              </Box>
            </Box>

            {/* Categories */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Categories</Typography>
              <FormGroup>
                {categories.map(cat => (
                  <FormControlLabel
                    key={cat}
                    control={
                      <Checkbox
                        checked={filters.categories.includes(cat)}
                        onChange={() => handleCategoryChange(cat)}
                      />
                    }
                    label={cat}
                  />
                ))}
              </FormGroup>
            </Box>

            {/* Ratings */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Ratings</Typography>
              <FormGroup>
                {ratings.map(rating => (
                  <FormControlLabel
                    key={rating}
                    control={
                      <Checkbox
                        checked={filters.ratings.includes(rating)}
                        onChange={() => handleRatingChange(rating)}
                      />
                    }
                    label={`${rating} stars & up`}
                  />
                ))}
              </FormGroup>
            </Box>

            {/* Brands */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Brands</Typography>
              <FormGroup>
                {brands.map(brand => (
                  <FormControlLabel
                    key={brand}
                    control={
                      <Checkbox
                        checked={filters.brands.includes(brand)}
                        onChange={() => handleBrandChange(brand)}
                      />
                    }
                    label={brand}
                  />
                ))}
              </FormGroup>
            </Box>

            <Button variant="outlined" fullWidth onClick={() => setFilters({ priceRange: [0, 500], categories: [], ratings: [], brands: [] })}>
              Clear Filters
            </Button>
          </Card>
        </Grid>

        {/* Product Grid */}
        <Grid item xs={12} md={9}>
          <Grid container spacing={2}>
            {displayedProducts.map((product, index) => (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                md={4} 
                key={product.id}
                ref={index === displayedProducts.length - 1 ? lastProductRef : null}
              >
                <Card
                  component={motion.div}
                  whileHover={{ y: -5, scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  sx={{ position: 'relative', height: '100%', cursor: 'pointer' }}
                  onClick={() => navigate('/ProductPage')}
                >
                  <CardMedia component="img" height="250" image={product.image} alt={product.title} />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>{product.title}</Typography>
                    <Typography variant="h5" color="primary" sx={{ mb: 1 }}>${product.price}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={16} fill={i < Math.floor(product.rating) ? 'gold' : 'none'} color="gold" />
                      ))}
                      <Typography variant="body2">({product.rating})</Typography>
                    </Box>
                    <Chip label={product.brand} size="small" sx={{ mb: 1 }} />
                    <Button 
                      variant="contained" 
                      fullWidth
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Add to Cart
                    </Button>
                  </CardContent>
                  <IconButton 
                    sx={{ position: 'absolute', top: 10, right: 10, bgcolor: 'white' }}
                    component={motion.button}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Heart size={20} />
                  </IconButton>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {/* Loading indicator */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>Loading more products...</Typography>
            </Box>
          )}
          
          {/* No products found */}
          {displayedProducts.length === 0 && !isLoading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6">No products found matching your filters.</Typography>
              <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setFilters({ priceRange: [0, 500], categories: [], ratings: [], brands: [] })}>
                Clear Filters
              </Button>
            </Box>
          )}
          
          {/* End of products message */}
          {!hasMore && displayedProducts.length > 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                You've seen all {filteredProducts.length} products
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default ShopPage;